import React from 'react';
import { compose } from 'redux';
import { array, arrayOf, bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { intlShape, injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);

const TODAY = new Date();
const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

/**
 * OrderPanel component - displays the booking form for scheduling mentoring sessions.
 * This component has been simplified to only use the BookingTimeForm since Sonispark
 * mentorship sessions are hourly bookings. Other form types (BookingDatesForm,
 * ProductOrderForm, InquiryWithoutPaymentForm) have been removed.
 */
const OrderPanel = props => {
  const {
    rootClassName = null,
    className = null,
    listing,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing = false,
    onSubmit,
    onFetchTimeSlots,
    monthlyTimeSlots = null,
    intl,
    onFetchTransactionLineItems,
    lineItems = null,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError = null,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { unitType } = publicData || {};
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <BookingTimeForm
        className={css.bookingForm}
        formId="OrderPanelBookingTimeForm"
        lineItemUnitType={lineItemUnitType}
        onSubmit={onSubmit}
        price={price}
        marketplaceCurrency={marketplaceCurrency}
        dayCountAvailableForBooking={dayCountAvailableForBooking}
        listingId={listing.id}
        isOwnListing={isOwnListing}
        monthlyTimeSlots={monthlyTimeSlots}
        onFetchTimeSlots={onFetchTimeSlots}
        startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
        endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
        timeZone={timeZone}
        marketplaceName={marketplaceName}
        onFetchTransactionLineItems={onFetchTransactionLineItems}
        lineItems={lineItems}
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        fetchLineItemsError={fetchLineItemsError}
      />
    </div>
  );
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,
  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,
  navigate: func,
  location: shape({
    search: string,
  }).isRequired,
  intl: intlShape.isRequired,
};

export default compose(injectIntl)(OrderPanel);
